import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { CalendarDays, Clock, MapPin, CalendarHeart } from "lucide-react";
import { toast } from "sonner";

const TYPE_STYLES = {
  Academic: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
  Sports: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  Cultural: "bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/40 dark:text-fuchsia-300",
  Celebration: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  PTM: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300",
  Excursion: "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300",
  Competition: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
  Other: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
};

export const eventBadgeClass = (t) => TYPE_STYLES[t] || TYPE_STYLES.Other;

export const fmtEventRange = (start, end) => {
  const opt = { day: "numeric", month: "short", year: "numeric" };
  try {
    const s = new Date(start + "T00:00:00").toLocaleDateString(undefined, opt);
    if (!end || end === start) return s;
    const e = new Date(end + "T00:00:00").toLocaleDateString(undefined, opt);
    return `${s} → ${e}`;
  } catch { return start; }
};

export default function EventsList({ endpoint = "/events" }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const year = new Date().getFullYear();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(endpoint, { params: { year } });
        setEvents(data.events || []);
      } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not load events"); }
      finally { setLoading(false); }
    })();
  }, [year, endpoint]);

  if (loading) return <div className="text-slate-500" data-testid="events-loading">Loading events…</div>;
  if (!events.length) return (
    <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 p-10 text-center text-slate-500" data-testid="events-empty">
      No events published for {year} yet.
    </div>
  );

  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="space-y-3" data-testid="events-list">
      {events.map((ev) => {
        const past = (ev.end_date || ev.start_date) < today;
        return (
          <div key={ev.id} className={`flex items-start gap-3 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 ${past ? "opacity-60" : ""}`} data-testid={`event-row-${ev.id}`}>
            <div className="h-10 w-10 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 flex items-center justify-center shrink-0">
              <CalendarHeart className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-900 dark:text-slate-100">{ev.title}</span>
                <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${eventBadgeClass(ev.type)}`}>{ev.type}</span>
              </div>
              <div className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> {fmtEventRange(ev.start_date, ev.end_date)}</span>
                {ev.time && <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {ev.time}</span>}
                {ev.venue && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {ev.venue}</span>}
              </div>
              {ev.description && <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">{ev.description}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
